/* eslint-disable @typescript-eslint/no-unused-expressions */
import { AfterViewInit, Component, Inject, OnDestroy, Renderer2 } from '@angular/core';
import { DataStorageService } from '@core/auth/services/data-storage.service';
import { FontColorContrastFnType, FONT_COLOR_CONTRAST } from '@core/injection-tokens';
import { CompanyCustomizationsService } from '@core/services/customization/customizations.service';
import { CustomizationReloadService } from '@core/services/customization/customization-reload.service';
import { Customization } from '@core/services/customization/models/customization';
import { AppStateService } from '@core/services/state.service';
import { TranslocoService } from '@ngneat/transloco';
import { AppendCustomCodeService } from '@shared/components/append-custom-code/append-custom-code.service';
import { asapScheduler, catchError, defaultIfEmpty, Observable, observeOn, of, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Component({
  standalone: false,
  selector: 'ac-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private dataStorage: DataStorageService,
    private customizations: CompanyCustomizationsService,
    private translocoService: TranslocoService,
    private appState: AppStateService,
    private appendCustomCodeService: AppendCustomCodeService,
    private renderer: Renderer2,
    private customizationReloadService: CustomizationReloadService,
    @Inject(FONT_COLOR_CONTRAST) private fontColorContrast: FontColorContrastFnType
  ) {
    this.initApp(this.customizations.getCustomizations());

    // Subscribe to reload requests from auth service; switchMap cancels a previous
    // still-retrying reload when a new request arrives
    this.customizationReloadService.reloadRequest$
      .pipe(
        switchMap(() => this.reloadCustomizationsAfterLogin()),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.customizationReloadService.notifyReloadComplete());
  }

  ngAfterViewInit(): void {
    this.appendCustomCodeService.injectCustomCode(this.renderer);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reloadCustomizationsAfterLogin(): Observable<void> {
    // no argument: post-bootstrap the service already caches the companyUuid, and keeping the
    // switchMap projector expression-free means nothing here can throw past the inner catchError
    return this.customizations.loadCustomizations().pipe(
      tap(() => {
        const custom = this.customizations.getCustomizations();
        this.setUserLanguage(custom);
        this.setCustomizedColors(custom);
      }),
      catchError((err: unknown) => {
        // swallow so the handshake still completes — login flows wait on reloadComplete$ and
        // must not hang; the session proceeds with the customizations loaded at bootstrap
        // eslint-disable-next-line no-console
        console.error('customizations reload after login failed', err);
        return of(void 0);
      }),
      // an EMPTY source (e.g. a request swallowed by the interceptor) must still notify
      defaultIfEmpty(void 0 as void),
      // defer the notify to a microtask even if the source emits/errors synchronously,
      // so it can never fire before the login flow subscribes to reloadComplete$
      observeOn(asapScheduler)
    );
  }

  private initApp(custom: Customization): void {
    this.initTransloco();
    this.setUserLanguage(custom);
    this.setCustomizedColors(custom);
    this.appState.observeScreenSizeChanges();
  }

  private initTransloco(): void {
    this.translocoService.langChanges$.pipe(takeUntil(this.destroy$)).subscribe((selectedLanguage) => {
      this.translocoService.setFallbackLangForMissingTranslation({
        fallbackLang: selectedLanguage.replace('-custom', '')
      });
    });
  }

  private setUserLanguage(custom: Customization): void {
    let language = this.dataStorage.getLang();
    if (!language) {
      language = custom.DEFAULT_LANG?.split(',')[0] || 'en';
    }
    this.translocoService.setActiveLang(language + '-custom');
    this.translocoService.setFallbackLangForMissingTranslation({ fallbackLang: language });
  }

  setCustomizedColors(custom: Customization): void {
    if (custom.APP_COLORS) {
      const appColors = JSON.parse(custom.APP_COLORS);
      for (const key of Object.keys(appColors)) {
        const colorVal = appColors[key];
        document.documentElement.style.setProperty(`--color-${key}`, colorVal);
        // set text color black or white for bg colors
        const contrastColorVal = this.fontColorContrast(colorVal);
        document.documentElement.style.setProperty(`--color-${key}-contrast`, contrastColorVal);

        const colorHasHoverColor = key === 'brand' || key === 'navbar' || key === 'lb';
        if (colorHasHoverColor) {
          document.documentElement.style.setProperty(`--color-${key}-hover`, this.setColorShade(colorVal, -50));
        }

        const colorHasLightVariant = key === 'supportive';
        if (colorHasLightVariant) {
          const lightColorVal = this.setColorShade(colorVal, 80);
          const lightColorContrastVal = this.fontColorContrast(lightColorVal);
          document.documentElement.style.setProperty(`--color-${key}-light`, lightColorVal);
          document.documentElement.style.setProperty(`--color-${key}-light-contrast`, lightColorContrastVal);
        }
      }
      // backwards compatibility
      if (!appColors['navbar']) {
        document.documentElement.style.setProperty(`--color-navbar`, appColors['brand']);
      }
      if (!appColors['navbar-hover']) {
        document.documentElement.style.setProperty(`--color-navbar-hover`, appColors['brand-hover']);
      }
    }
  }

  // https://natclark.com/tutorials/javascript-lighten-darken-hex-color/
  // Simple method for calculating lighter or darker shades
  // magnitude > 0 => lighter/ magnitude < 0 => darker
  private setColorShade(hexColor: any, magnitude: number): any {
    hexColor = hexColor.replace(`#`, ``);
    if (hexColor.length === 6) {
      const decimalColor = parseInt(hexColor, 16);
      let r = (decimalColor >> 16) + magnitude;
      r > 255 && (r = 255);
      r < 0 && (r = 0);
      let g = (decimalColor & 0x0000ff) + magnitude;
      g > 255 && (g = 255);
      g < 0 && (g = 0);
      let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
      b > 255 && (b = 255);
      b < 0 && (b = 0);
      let newHoverColor = `#${(g | (b << 8) | (r << 16)).toString(16)}`;
      const newHoverColorLength = newHoverColor.length;
      if (newHoverColorLength < 7) {
        for (let index = 0; index < 7 - newHoverColorLength; index++) {
          newHoverColor = newHoverColor.substring(0, 1) + '0' + newHoverColor.substring(1, newHoverColor.length);
        }
      }
      return newHoverColor;
    } else {
      return hexColor;
    }
  }
}
